import { yupResolver } from "@hookform/resolvers/yup";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router";
import Button from "../../../../components/Common/Button/Button";
import TabLinks from "../../../../components/Common/TabLinks/TabLinks";
import AddSurveyAnswerArr from "../../../../components/Manage/CreateShotClass/AddSurveyAnswerArr/AddSurveyAnswerArr";
import { FaSave } from "react-icons/fa";
import AddSurveyQuestionArr from "../../../../components/Manage/CreateShotClass/AddSurveyQuestionArr/AddSurveyQuestionArr";
import { createShotclasses } from "../../../../utils/mainTabs/manage/createShotclasses";
import {
  basicSurveySchema,
  scoredSurveySchema,
} from "../../../../utils/mainTabs/manage/createShotclassSchema";
import classes from "./AddSurvey.module.scss";
import PlusIcon from "../../../../components/Common/SvgIcons/CommonIcons/PlusIcon";
import { useDispatch, useSelector } from "react-redux";
import PreviousIcon from "../../../../components/Common/SvgIcons/CommonIcons/PreviousIcon";
import {
  copyShotclassActionHandler,
  createShotclassActionHandler,
  updateShotclassActionHandler,
} from "../../../../store/actions/actionHandlers/manageActionHandler";
import { setErrorHandler } from "../../../../store/actions/actionHandlers/errorActionHandler";
import { errorTypes } from "../../../../utils/errorTypes";
import loaders from "../../../../store/actions/actionCreators/loadersActionCreator";
import { submisssions } from "../../../../utils/mainTabs/social/submissions";
import { setShotClassFormData } from "../../../../store/actions/actionCreators/socialActionCreator";
import { FormattedMessage } from "react-intl";
import { moderateContributions } from "../../../../utils/mainTabs/social/moderateContributions";
import Input from "../../../../components/Common/Input/Input";
import ScoreBandConfig from "../../../../components/Manage/ScoreBandConfiguration/ScoreBandConfig";
import Modal from "../../../../components/Common/Modal/Modal";
import RadioGroup from "../../../../components/Common/RadioGroup/RadioGroup";
// import { usePrompt } from "../../../../hooks/preventNavigationHook";

const AddSurvey = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [saveTrue, setSaveTrue] = useState(false);
  const [normalExit, setNormalExit] = useState(false);
  const [disableTabsOnValid, setDisableTabsOnValid] = useState(false);
  const params = useParams();
  const {
    tenantName,
    initialFormState,
    formValues,
    setFormValues,
    setManageTiles,
    shotclassType,
    managePath,
    createShotclassesPath,
    shotclassTypeFrom,
  } = useOutletContext();

  const manageState = useSelector((state) => state.manageReducer);
  const { saveAsADraftLoader } = manageState;
  const commonState = useSelector((state) => state.commonReducer);
  const { colors } = commonState;

  const [surveyDataDeleteOnToggleWarning, setSurveyDataDeleteOnToggleWarning] =
    useState(false);
  const [pendingSurveyType, setPendingSurveyType] = useState(null);
  const [isEditQuestion, setIsEditQuestion] = useState(false);
  const [surveyType, setSurveyType] = useState(
    shotclassType === "edit" && formValues?.Other?.Survey?.Type === 1
      ? "scored"
      : "basic"
  ); // Default to basicTabs
  let answerTabs = [
    {
      id: 1,
      name: <FormattedMessage id="CREATE_MUTIPLECHOICE_TEXT" />,
      disabled:
        // shotclassType === "edit" ||
        // shotclassType === "copy" ||
        // shotclassType === "view" ||
        isEditQuestion || disableTabsOnValid || formValues?.disableTabsOnValid,
    },
    {
      id: 2,
      name: <FormattedMessage id="CREATE_QUIZ_T&F_TEXT" />,
      disabled:
        // shotclassType === "edit" ||
        // shotclassType === "copy" ||
        // shotclassType === "view" ||
        isEditQuestion || disableTabsOnValid || formValues?.disableTabsOnValid,
    },
  ];
  const [activeTab, setActiveTab] = useState(1);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);

  let buttonTexts = useMemo(() => {
    return {
      addDetails: "Add Details",
      addQuiz: "Add Quiz",
    };
  }, []);
  const [backButtonText, setBackButtonText] = useState(buttonTexts.addQuiz);

  useEffect(() => {
    if (formValues?.Other?.IsDistributed) {
      if (
        formValues?.Other?.ShotClassType === 3 ||
        formValues?.Other?.ShotClassType === 5
      ) {
        setBackButtonText(buttonTexts.addQuiz);
      } else if (
        formValues?.Other?.ShotClassType === 4 ||
        formValues?.Other?.ShotClassType === 7
      ) {
        setBackButtonText(buttonTexts.addDetails);
      }
      // if (formValues?.addQuiz?.TotalQuestionCount > 0) {
      //   setBackButtonText(buttonTexts.addQuiz);
      // } else if (formValues?.addQuiz?.TotalQuestionCount === 0) {
      //   setBackButtonText(buttonTexts.addDetails);
      // }
    }
  }, [
    buttonTexts,
    formValues?.Other?.IsDistributed,
    formValues?.addQuiz?.TotalQuestionCount,
    formValues?.Other?.ShotClassType,
  ]);

  // Restore survey type from formValues when coming back from other pages
  useEffect(() => {
    if (
      formValues?.addSurvey?.surveyType &&
      formValues.addSurvey.surveyType !== surveyType
    ) {
      if (formValues.addSurvey.TotalQuestionCount > 0)
        setSurveyType(formValues.addSurvey.surveyType);
      else setSurveyType("basic");
    }
  }, [formValues?.addSurvey?.surveyType]);

  // ✅ Add this useEffect to automatically reset survey type when no questions remain
  useEffect(() => {
    const currentQuestionCount = formValues?.addSurvey?.TotalQuestionCount || 0;

    // If scored survey has no questions, reset to basic
    if (
      surveyType === "scored" &&
      currentQuestionCount === 0 &&
      !formValues?.Other?.IsDistributed
    ) {
      setSurveyType("basic");

      // Update formValues to reflect the change
      setFormValues((prevFormValues) => ({
        ...prevFormValues,
        addSurvey: {
          ...prevFormValues.addSurvey,
          surveyType: "basic",
        },
      }));
    }
  }, []);

  const methods = useForm({
    defaultValues: {
      ...formValues.addSurvey,
    },
    mode: "onChange",
    resolver: yupResolver(
      surveyType === "basic" ? basicSurveySchema : scoredSurveySchema
    ),
  });
  const {
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
    control,
    trigger,
    watch,
    getValues,
    clearErrors,
    register,
  } = methods;

  // ✅ Handle validation when survey type changes
  const prevSurveyTypeRef = useRef(surveyType);
  useEffect(() => {
    // Only re-validate if survey type actually changed and we have form data
    if (prevSurveyTypeRef.current !== surveyType && surveyType) {
      const currentData = getValues();
      const hasQuestions = currentData.QuestionsList?.length > 0;
      const hasValidQuestion = currentData.QuestionsList?.[0]?.Question?.trim();

      if (hasQuestions && hasValidQuestion) {
        // Small delay to ensure schema update is complete
        setTimeout(() => {
          trigger();
        }, 100);
      }
    }
    prevSurveyTypeRef.current = surveyType;
  }, [surveyType]);

  const questionListWatch = watch("QuestionsList");

  // append, prepend, remove, swap, move, insert
  const {
    fields: questionsFields,
    append: appendQuestion,
    remove: removeQuestion,
    update: updateQuestion,
    move: moveQuestion,
  } = useFieldArray({
    control,
    name: "QuestionsList",
  });

  const {
    fields: scoreBandFields,
    append: scoreBandAppend,
    remove: scoreBandRemove,
    update: scoreBandUpdate,
  } = useFieldArray({
    control,
    name: "scoreBands",
  });
  console.log("error", errors);

  const addSurveyCompleted = useCallback(
    (isCompleted) => {
      let updatedTiles = createShotclasses
        .tiles(navigate, location, params, tenantName)
        .map((tile) => {
          if (
            tile.path === `${createShotclassesPath}/details` ||
            tile.path ===
              `${submisssions.shotClassespath(
                params,
                location,
                tenantName
              )}/details` ||
            ((tile.path === `${createShotclassesPath}/addQuiz` ||
              tile.path ===
                `${submisssions.shotClassespath(
                  params,
                  location,
                  tenantName
                )}/addQuiz`) &&
              formValues?.addQuiz?.TotalQuestionCount > 0)
          ) {
            return { ...tile, isCompleted: true };
          } else if (
            tile.path === `${createShotclassesPath}/addSurvey` ||
            tile.path ===
              `${submisssions.shotClassespath(
                params,
                location,
                tenantName
              )}/addSurvey`
          ) {
            return { ...tile, isCompleted: isCompleted };
          } else {
            return { ...tile, isCompleted: false };
          }
        });
      setManageTiles(updatedTiles);
    },
    [
      tenantName,
      createShotclassesPath,
      formValues?.addQuiz?.TotalQuestionCount,
      navigate,
      location,
      params,
      setManageTiles,
    ]
  );

  //harshil bhai code
  // const handleClearAll = useCallback(() => {
  //   reset(() => initialFormState.addSurvey);
  //   setFormValues((formValues) => {
  //     return {
  //       ...formValues,
  //       addSurvey: initialFormState.addSurvey,
  //     };
  //   });
  //   setActiveTab(1);
  //   setActiveQuestionIndex(() => 0);
  //   setIsEditQuestion(false);
  // }, [initialFormState.addSurvey, reset, setFormValues]);
  //mukesh code
  const handleClearAll = useCallback(() => {
    // reset(() => initialFormState.addQuiz);
    // setFormValues((formValues) => {
    //   return {
    //     ...formValues,
    //     addQuiz: initialFormState.addQuiz,
    //   };
    // });
    // setActiveTab(1);
    // setActiveQuestionIndex(() => 0);
    // setIsEditQuestion(false);

    //mukesh code
    if (
      activeQuestionIndex >= 0 ||
      (activeQuestionIndex === 0 && questionListWatch.length > 1)
    ) {
      clearErrors(); // Clear all errors
      const updatedQuestions = questionsFields.map((field, index) => {
        if (index === activeQuestionIndex) {
          return {
            Question: "",
            ActiveQuestionType: 1,
            MultiSelect: false,
            IsFreeFormTextBox: false,
            AnswersList: [{ Answer: "" }, { Answer: "" }],
          };
        }
        return field;
      });
      reset({ QuestionsList: updatedQuestions });
      setActiveTab(1);
      setIsEditQuestion(false);
      setDisableTabsOnValid(false);

      setFormValues((formValues) => {
        return {
          ...formValues,
          disableTabsOnValid: false,
        };
      });
    }
    if (activeQuestionIndex === 0 && questionListWatch.length === 1) {
      reset(() => initialFormState.addQuiz);
      setFormValues((formValues) => {
        return {
          ...formValues,
          addQuiz: initialFormState.addQuiz,
        };
      });
      setActiveTab(1);
      setActiveQuestionIndex(() => 0);
      setIsEditQuestion(false);
    }
    // }, [initialFormState.addQuiz, reset, setFormValues]);

    //mukesh dependency
  }, [
    activeQuestionIndex,
    reset,
    clearErrors,
    questionsFields,
    setActiveTab,
    setIsEditQuestion,
  ]);
  useEffect(() => {
    if (formValues?.addSurvey?.TotalQuestionCount > 0) {
      setActiveQuestionIndex(() => -1);
    } else {
      addSurveyCompleted(false);
      setActiveQuestionIndex(() => 0);
    }
  }, [formValues?.addSurvey?.TotalQuestionCount, addSurveyCompleted]);

  useEffect(() => {
    if (formValues?.addSurvey?.QuestionsList?.length === 0) {
      // Preserve the current surveyType when resetting
      const resetData = {
        ...initialFormState.addSurvey,
        surveyType: surveyType, // ✅ Preserve current survey type
      };

      reset(() => resetData);
      setFormValues((formValues) => {
        return {
          ...formValues,
          addSurvey: resetData, // ✅ Use resetData instead of initialFormState
        };
      });

      // Don't reset surveyType to "basic" - keep current selection
      // setSurveyType("basic"); // ❌ REMOVE THIS LINE
    }
  }, [
    formValues?.addSurvey?.QuestionsList?.length,
    initialFormState.addSurvey,
    reset,
    setFormValues,
    surveyType, // ✅ Add surveyType to dependencies
  ]);

  const handleSkipSurvey = () => {
    if (
      location?.pathname?.includes(
        `${submisssions.viewPath(tenantName)}/${params?.shotclassId}`
      ) ||
      location?.pathname?.includes(
        `${submisssions.updatePath(tenantName)}/${params?.shotclassId}`
      )
    )
      navigate(
        `${submisssions.shotClassespath(
          params,
          location,
          tenantName
        )}/publishShotclass`
      );
    else navigate(`${createShotclassesPath}/publishShotclass`);
  };

  const handleSetActiveTab = (id) => {
    if (!isEditQuestion) {
      const updatedQuestion = {
        Question: questionListWatch?.[activeQuestionIndex]?.Question,
        ActiveQuestionType: id,
        MultiSelect: false,
        AnswersList: [{ Answer: "" }, { Answer: "" }],
      };
      updateQuestion(
        activeQuestionIndex,
        //   {
        //   Question: questionListWatch?.[activeQuestionIndex]?.Question,
        //   AnswersList: questionListWatch?.[activeQuestionIndex]?.AnswersList,
        //   ActiveQuestionType: id,
        // }
        updatedQuestion
      );
      setActiveTab(() => id);
    }
  };
  // useEffect(() => {
  //   if (formValues?.addSurvey?.TotalQuestionCount > 0) {
  //     setFormValues((formValues) => {
  //       return {
  //         ...formValues,
  //         publish: {
  //           IsSurveyMandatory: formValues?.publish?.IsSurveyMandatory,
  //         },
  //       };
  //     });
  //   }
  // }, [
  //   setFormValues,
  //   formValues?.publish?.IsSurveyMandatory,
  //   formValues?.addQuiz?.TotalQuestionCount,
  // ]);

  console.log("formvalues", formValues);

  const onSubmit = (data) => {
    addSurveyCompleted(true);
    const surveyData = {
      ...data,
      surveyType: surveyType,
      TotalQuestionCount: data?.QuestionsList?.length,
    };
    setFormValues((formValues) => {
      return {
        ...formValues,
        addSurvey: {
          ...formValues.addSurvey,
          ...surveyData,
        },
        publish: {
          IsPublishPageVisited: true,
          ...formValues.publish,
          ...surveyData,
          // IsSurveyMandatory:
          //   formValues.addSurvey?.TotalQuestionCount > 0 ? true : false,
        },
      };
    });
    if (
      location?.pathname?.includes(
        `${submisssions.viewPath(tenantName)}/${params?.shotclassId}`
      ) ||
      location?.pathname?.includes(
        `${submisssions.updatePath(tenantName)}/${params?.shotclassId}`
      )
    ) {
      dispatch(
        setShotClassFormData({
          ...formValues,
          addSurvey: {
            ...formValues.addSurvey,
            ...data,
            TotalQuestionCount: data?.QuestionsList?.length,
          },
          publish: {
            ...formValues.publish,
            ...data,
            // IsSurveyMandatory:
            //   formValues.addSurvey?.TotalQuestionCount > 0 ? true : false,
          },
        })
      );
      navigate(
        `${submisssions.shotClassespath(
          params,
          location,
          tenantName
        )}/publishShotclass`
      );
    } else navigate(`${createShotclassesPath}/publishShotclass`);
  };

  // ✅ Add this useEffect after the existing ones
  // useEffect(() => {
  //   // When survey type changes, ensure tabs and checkboxes are enabled for basic surveys
  //   if (surveyType === "basic" && !formValues?.Other?.IsDistributed) {
  //     setDisableTabsOnValid(false);
  //     setFormValues((prevFormValues) => ({
  //       ...prevFormValues,
  //       disableTabsOnValid: false,
  //     }));
  //   }
  // }, [surveyType, formValues?.Other?.IsDistributed, setFormValues]);

  const handleDrag = ({ source, destination }) => {
    if (destination) {
      moveQuestion(source.index, destination.index);
    }
  };

  const handleDeleteSurveyQuestion = (index) => {
    removeQuestion(index);
    setActiveTab(1);

    let surveyData = getValues();
    surveyData = {
      ...surveyData,
      surveyType: surveyType, // ✅ Preserve the current survey type
      TotalQuestionCount: surveyData.QuestionsList?.length,
    };
    setFormValues((formValues) => {
      return {
        ...formValues,
        addSurvey: {
          ...formValues.addSurvey,
          ...surveyData,
        },
      };
    });

    setIsEditQuestion(false);
    setDisableTabsOnValid(false);
    setFormValues((formValues) => {
      return {
        ...formValues,
        disableTabsOnValid: false,
      };
    });
    setActiveQuestionIndex(() => -1);
  };

  const performSurveyTypeChange = (type) => {
    if (!formValues?.Other?.IsDistributed) {
      console.log("Survey type changed to:", type);

      // Save current form data before switching (if needed for segregation)
      const currentFormData = getValues();

      // Create the appropriate initial structure based on survey type
      const newSurveyStructure = {
        QuestionsList: [
          {
            Question: "",
            ActiveQuestionType: 1,
            MultiSelect: false,
            IsFreeFormTextBox: false,
            AnswersList:
              type === "scored"
                ? [
                    { Answer: "", Score: 0 },
                    { Answer: "", Score: 0 },
                  ]
                : [{ Answer: "" }, { Answer: "" }],
            ...(type === "scored" && { TotalScore: 0 }),
          },
        ],
        TotalQuestionCount: 0,
        surveyType: type,
      };

      // Update the survey type state
      setSurveyType(type);

      // Reset the React Hook Form with new structure
      reset(newSurveyStructure);

      // Update formValues to stay in sync
      setFormValues((prevFormValues) => ({
        ...prevFormValues,
        addSurvey: {
          ...prevFormValues.addSurvey,
          ...newSurveyStructure,
        },
      }));

      // Reset UI state
      setActiveQuestionIndex(0);
      setActiveTab(1);
      setIsEditQuestion(false);
      // setDisableTabsOnValid(false);
      if (type === "basic" && !formValues?.Other?.IsDistributed) {
        setDisableTabsOnValid(false);
        setFormValues((prevFormValues) => ({
          ...prevFormValues,
          disableTabsOnValid: false,
        }));
      }
    }
  };

  const handleConfirmSurveyTypeChange = () => {
    if (pendingSurveyType) {
      performSurveyTypeChange(pendingSurveyType); // ✅ Apply the pending change
      setPendingSurveyType(null); // ✅ Clear pending type
    }
    setSurveyDataDeleteOnToggleWarning(false); // ✅ Close modal
  };

  const handleCancelSurveyTypeChange = () => {
    setPendingSurveyType(null); // ✅ Clear pending type
    setSurveyDataDeleteOnToggleWarning(false); // ✅ Close modal
  };

  const handleSurveyTypeChange = (type) => {
    const hasWork =
      isDirty ||
      questionListWatch?.length > 1 ||
      (questionListWatch?.[0]?.Question &&
        questionListWatch[0].Question.trim() !== "");

    if (hasWork && !formValues?.Other?.IsDistributed) {
      setPendingSurveyType(type);
      setSurveyDataDeleteOnToggleWarning(true);
    } else {
      performSurveyTypeChange(type);
    }
  };

  const saveQuestion = () => {
    debugger;
    if (shotclassType === "view") {
      setActiveQuestionIndex((_) => -1);
    } else {
      // Validate only the current question being edited, not score bands
      trigger("QuestionsList").then((isQuestionValid) => {
        if (isQuestionValid) {
          let surveyData = getValues();
          surveyData = {
            ...surveyData,
            surveyType: surveyType, // ✅ Preserve the current survey type
            TotalQuestionCount: surveyData.QuestionsList?.length,
          };
          setFormValues((formValues) => {
            return {
              ...formValues,
              addSurvey: {
                ...formValues.addSurvey,
                ...surveyData,
              },
              publish: {
                ...formValues.publish,
                ...surveyData,
                IsSurveyMandatory: formValues?.publish?.IsPublishPageVisited
                  ? formValues?.publish?.IsSurveyMandatory
                  : surveyData.TotalQuestionCount > 0
                  ? true
                  : false,
                // ✅ Set IsTrackingEnabled to true by default for scored surveys
                IsTrackingEnabled:
                  surveyData.surveyType === "scored" &&
                  surveyData.TotalQuestionCount > 0
                    ? true
                    : formValues?.publish?.IsTrackingEnabled,
              },
            };
          });
          setActiveQuestionIndex((_) => -1);
          setDisableTabsOnValid(true);
        }
      });
      setIsEditQuestion(false);
    }
  };
  const handleBack = () => {
    if (
      location?.pathname?.includes(
        `${submisssions.viewPath(tenantName)}/${params?.shotclassId}`
      ) ||
      location?.pathname?.includes(
        `${submisssions.updatePath(tenantName)}/${params?.shotclassId}`
      )
    )
      navigate(
        `${submisssions.shotClassespath(params, location, tenantName)}/addQuiz`
      );
    else {
      if (isValid && saveTrue) {
        let surveyData = getValues();
        surveyData = {
          ...surveyData,
          surveyType: surveyType, // ✅ Preserve the current survey type
          TotalQuestionCount: surveyData.QuestionsList?.length,
        };
        setFormValues((formValues) => {
          return {
            ...formValues,
            addSurvey: {
              ...formValues.addSurvey,
              ...surveyData,
            },
          };
        });
      } else {
        let surveyData = getValues();
        if (surveyData.surveyType === "scored") {
          let surveyData = getValues();
          surveyData = {
            ...surveyData,
            surveyType: surveyType,
          };

          setFormValues((formValues) => {
            // Only save survey values if saveTrue === true, but always save scoreBands if present
            return {
              ...formValues,
              addSurvey: {
                ...(saveTrue
                  ? { ...formValues.addSurvey, ...surveyData }
                  : { ...formValues.addSurvey }),
                ...(surveyData.scoreBands
                  ? { scoreBands: surveyData.scoreBands }
                  : {}),
                // ✅ Always preserve TotalQuestionCount from current formValues
                TotalQuestionCount:
                  formValues.addSurvey?.TotalQuestionCount ||
                  surveyData.TotalQuestionCount ||
                  0,
                // ✅ Always preserve surveyType
                surveyType: surveyType,
              },
            };
          });
        }
      }
      setFormValues((formValues) => {
        return {
          ...formValues,
          disableTabsOnValid:
            formValues?.addQuiz?.TotalQuestionCount == 0 ? false : true,
        };
      });
      if (backButtonText === buttonTexts.addDetails) {
        navigate(`${createShotclassesPath}/details`);
      } else if (backButtonText === buttonTexts.addQuiz) {
        navigate(`${createShotclassesPath}/addQuiz`);
      }
    }
  };

  const handleSaveAsDraft = () => {
    dispatch(loaders.manage.createShotclasses.saveAsADraftLoader(true));
    let surveyData = getValues();
    if (activeQuestionIndex !== -1) {
      let questionsList = surveyData.QuestionsList?.filter(
        (_, index) => index !== activeQuestionIndex
      );
      surveyData = {
        ...surveyData,
        surveyType: surveyType, // ✅ Preserve the current survey type
        QuestionsList: questionsList,
        TotalQuestionCount: questionsList?.length,
      };
    } else {
      surveyData = {
        ...surveyData,
        surveyType: surveyType, // ✅ Preserve the current survey type
        TotalQuestionCount: surveyData.QuestionsList?.length,
      };
    }
    let shotclassData = { ...formValues, addSurvey: surveyData };
    if (shotclassType === "edit") {
      dispatch(updateShotclassActionHandler(shotclassData, "Saved"))
        .then((isSaved) => {
          if (isSaved) {
            if (isSaved !== "video") {
              dispatch(
                setErrorHandler(
                  errorTypes.SUCCESS,
                  `Your Shotclass ${shotclassData?.details?.ShotClassName} is saved successfully`
                )
              );
            }
            dispatch(
              loaders.manage.createShotclasses.saveAsADraftLoader(false)
            );
            navigate(managePath);
          }
        })
        .finally(() => {
          dispatch(loaders.manage.createShotclasses.saveAsADraftLoader(false));
        });
    } else if (shotclassType === "copy") {
      dispatch(copyShotclassActionHandler(shotclassData, "Saved"))
        .then((isSaved) => {
          if (isSaved) {
            if (isSaved !== "video") {
              dispatch(
                setErrorHandler(
                  errorTypes.SUCCESS,
                  `Your Shotclass ${shotclassData?.details?.ShotClassName} is saved successfully`
                )
              );
            }
            dispatch(
              loaders.manage.createShotclasses.saveAsADraftLoader(false)
            );
            navigate(managePath);
          }
        })
        .finally(() => {
          dispatch(loaders.manage.createShotclasses.saveAsADraftLoader(false));
        });
    } else {
      dispatch(
        createShotclassActionHandler(
          shotclassData,
          "Saved",
          "LearningContentWithQuizAndSurvey"
        )
      )
        .then((isSaved) => {
          if (isSaved) {
            if (isSaved !== "video") {
              dispatch(
                setErrorHandler(
                  errorTypes.SUCCESS,
                  `Your Shotclass ${shotclassData?.details?.ShotClassName} is saved successfully`
                )
              );
            }
            dispatch(
              loaders.manage.createShotclasses.saveAsADraftLoader(false)
            );
            navigate(managePath);
          }
        })
        .finally(() => {
          dispatch(loaders.manage.createShotclasses.saveAsADraftLoader(false));
        });
    }
  };
  // useEffect(() => {
  //   if (isDirty) setNormalExit(true);
  // }, [setNormalExit, isDirty]);

  // usePrompt("Are you sure you want to leave this page?", normalExit);

  console.log(
    "isValid || activeQuestionIndex !== -1",
    isValid,
    activeQuestionIndex
  );

  return (
    <>
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={classes.addSurvey__form}
        >
          <div className={classes.addSurvey__form__main}>
            <div className={classes.addSurvey__form__main__questionList}>
              {/* Survey Type Selection */}
              <RadioGroup
                label="Survey Type"
                name="surveyTypeRadio"
                options={[
                  { value: "basic", label: "Basic Survey" },
                  { value: "scored", label: "Scored Survey" },
                ]}
                value={surveyType}
                onChange={handleSurveyTypeChange}
                disabled={formValues?.Other?.IsDistributed}
                direction="horizontal"
                className={
                  classes.addSurvey__form__main__questionList__radioSection
                }
              />

              <div
                className={classes.addSurvey__form__main__questionList__heading}
              >
                {surveyType === "basic"
                  ? "Add survey with answers"
                  : "Add scored survey with answers"}
              </div>
              <AddSurveyQuestionArr
                questionsFields={questionsFields}
                appendQuestion={appendQuestion}
                errors={errors}
                activeQuestionIndex={activeQuestionIndex}
                setActiveQuestionIndex={setActiveQuestionIndex}
                isValid={isValid}
                shotclassType={shotclassType}
                questionListWatch={questionListWatch}
                setActiveTab={setActiveTab}
                isDistributed={formValues?.Other?.IsDistributed}
                trigger={trigger}
                isEditQuestion={isEditQuestion}
                updateQuestion={updateQuestion}
                setIsEditQuestion={setIsEditQuestion}
                handleDeleteSurveyQuestion={handleDeleteSurveyQuestion}
                disableTabsOnValid={disableTabsOnValid}
                setDisableTabsOnValid={setDisableTabsOnValid}
                handleDisableFormValue={formValues?.disableTabsOnValid}
                surveyType={surveyType}
                isScored={surveyType === "scored" ? true : false}
                formValues={formValues}
              />

              {activeQuestionIndex === -1 ? (
                !formValues?.Other?.IsDistributed ? (
                  <>
                    <Button
                      label={"Add New Question"}
                      height="3"
                      width="17.9"
                      icon={PlusIcon}
                      onClick={() => {
                        const newQuestion = {
                          Question: "",
                          ActiveQuestionType: 1,
                          MultiSelect: false,
                          IsFreeFormTextBox: false,
                          AnswersList:
                            surveyType === "scored"
                              ? [
                                  { Answer: "", Score: 0 },
                                  { Answer: "", Score: 0 },
                                ]
                              : [{ Answer: "" }, { Answer: "" }],
                        };
                        if (surveyType === "scored") {
                          newQuestion.TotalScore = 0;
                        }
                        appendQuestion(newQuestion);
                        setActiveQuestionIndex(
                          () => questionsFields?.length || 0
                        );
                        setIsEditQuestion(false);
                        setDisableTabsOnValid(false);
                        setFormValues((formValues) => {
                          return {
                            ...formValues,
                            disableTabsOnValid: false,
                          };
                        });
                        setActiveTab(() => 1);
                      }}
                      disabled={shotclassType === "view"}
                      light
                      type="button"
                    />
                  </>
                ) : (
                  <></>
                )
              ) : (
                <Button
                  label={<FormattedMessage id="SAVE_TXT" />}
                  height="3"
                  width="15.9"
                  icon={FaSave}
                  onClick={() => {
                    saveQuestion();
                    setSaveTrue(true);
                  }}
                  light
                  type="button"
                  disabled={shotclassType == "view"}
                />
              )}
            </div>
            {activeQuestionIndex !== -1 &&
              !questionListWatch?.[activeQuestionIndex]?.IsFreeFormTextBox && (
                <div className={classes.addSurvey__form__main__answerList}>
                  <div
                    className={
                      classes.addSurvey__form__main__answerList__header
                    }
                  >
                    <TabLinks
                      tabs={
                        surveyType === "basic"
                          ? answerTabs
                          : answerTabs.filter((tab) => tab.id !== 2)
                      }
                      activeTab={activeTab}
                      setActiveTab={(id) => {
                        clearErrors();
                        handleSetActiveTab(id);
                      }}
                    />
                    <div
                      className={classes.addSurvey__form__button__nextButton}
                      onClick={() => handleSkipSurvey()}
                    >
                      <div
                        className={
                          classes.addSurvey__form__button__nextButton__linkMedium
                        }
                      >
                        Skip Survey
                      </div>
                      <PreviousIcon fill={colors.theme} />
                    </div>
                  </div>
                  {questionsFields.map((_, questionIndex) => {
                    return questionIndex === activeQuestionIndex ? (
                      <AddSurveyAnswerArr
                        key={_.id}
                        activeQuestionIndex={activeQuestionIndex}
                        control={control}
                        errors={errors}
                        activeTab={activeTab}
                        shotclassType={shotclassType}
                        trigger={trigger}
                        isValid={isValid}
                        isDistributed={formValues?.Other?.IsDistributed}
                        surveyType={surveyType}
                        isScored={surveyType === "scored"}
                      />
                    ) : (
                      <React.Fragment key={_.id}></React.Fragment>
                    );
                  })}
                </div>
              )}
          </div>
          {surveyType === "scored" && activeQuestionIndex === -1 && (
            <ScoreBandConfig
              fields={scoreBandFields}
              append={scoreBandAppend}
              remove={scoreBandRemove}
              errors={errors}
              register={register}
              control={control}
              formValues={formValues}
              trigger={trigger}
              shotclassType={shotclassType}
            />
          )}
          <div className={classes.addSurvey__form__button}>
            <div
              className={classes.addSurvey__form__button__backButton}
              onClick={() => {
                handleBack();
                // setNormalExit(false);
              }}
            >
              <PreviousIcon fill={colors.theme} />
              <div
                className={
                  classes.addSurvey__form__button__backButton__linkMedium
                }
              >
                {/* {backButtonText} */}
                Previous
              </div>
            </div>
            <div className={classes.addSurvey__form__button__rightSection}>
              {shotclassType === "create" && (
                // shotclassType !== "view" &&
                //   (isDirty || formValues?.addSurvey?.TotalQuestionCount > 0)
                <div
                  className={classes.addSurvey__form__button__linkSmall}
                  onClick={() => handleClearAll()}
                >
                  <FormattedMessage id="CLEAR_ALL_TXT" />
                </div>
              )}
              {shotclassType !== "view" &&
                (shotclassType !== "edit" ||
                  (shotclassType === "edit" &&
                    shotclassTypeFrom === "myDrafts")) &&
                !location.pathname.includes(
                  moderateContributions.path(tenantName)
                ) && (
                  <Button
                    label={
                      saveAsADraftLoader ? (
                        "Saving..."
                      ) : (
                        <FormattedMessage id="SAVE_CLASS_TEXT" />
                      )
                    }
                    small
                    dark
                    loading={saveAsADraftLoader}
                    onClick={() => {
                      handleSaveAsDraft();
                      //setNormalExit(false);
                    }}
                  />
                )}
              <Button
                label="Next"
                small
                dark
                type="submit"
                disabled={!isValid || activeQuestionIndex !== -1}
                //onClick={() => setNormalExit(false)}
              />
            </div>
          </div>
        </form>
        <Modal
          handleSubmit={handleConfirmSurveyTypeChange}
          isOpen={surveyDataDeleteOnToggleWarning}
          heading={<FormattedMessage id="USERGROUP_DELETEGRP__HEADERMSG" />}
          toggle={handleCancelSurveyTypeChange}
          buttonLabel1={<FormattedMessage id="YES_TEXT" />}
          buttonLabel2={<FormattedMessage id="USERGRP_CANCELBTN_TEXT" />}
          width={"46.2"}
          height={"26.9"}
        >
          <div className={classes.addSurvey__surveyDataDeleteToglePopup}>
            <div className={classes.surveyDataDeleteToglePopup__text}>
              Entered survey data will be deleted. You may need to add it again.
            </div>
          </div>
        </Modal>
      </FormProvider>
    </>
  );
};

export default AddSurvey;
